/**
 * Step 3 sub — Meta dataset picker.
 *
 * 2026-05-22 redesign: shell wrap + restrained picker selection styling
 * (dark border + shadow + green check).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, AlertCircle, ExternalLink } from 'lucide-react';

import { useAuth } from '@/lib/useAuth';
import { apiRequest, ApiError } from '@/lib/api';
import { computeRailState } from '@/lib/onboardingState';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { cn } from '@/lib/utils';

interface DatasetInfo {
  id: string;
  name: string;
  last_fired_time: string | null;
  ad_account_count: number;
}

interface DatasetsResponse {
  datasets: DatasetInfo[];
}

export function OnboardingFacebookPickPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [datasets, setDatasets] = useState<DatasetInfo[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (me?.facebook_pixel_id) {
      navigate('/onboarding/connect/facebook-setup', { replace: true });
    }
  }, [me?.facebook_pixel_id, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<DatasetsResponse>('/oauth/facebook/datasets');
        if (cancelled) return;
        setDatasets(res.datasets);
        if (res.datasets.length === 1) {
          setSelected(res.datasets[0].id);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 400) {
          navigate('/onboarding/connect?facebook_error=picker_load_failed', { replace: true });
          return;
        }
        setLoadError("We couldn't load your Meta datasets. Try again or reconnect.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleContinue() {
    if (!selected || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiRequest('/oauth/facebook/select-dataset', {
        method: 'POST',
        body: { dataset_id: selected },
      });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/onboarding/connect/facebook-setup', { replace: true });
    } catch (e) {
      setSubmitError(
        e instanceof ApiError && e.detail
          ? e.detail
          : "We couldn't save your selection. Please try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      current={3}
      title="Pick your Meta dataset"
      subtitle="Choose which Meta dataset (formerly called a Pixel) HeyMotek sends WhatsApp conversion events to. Lead, Qualified, and Purchase events fire as your contacts progress."
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-5">
        {datasets === null && !loadError && (
          <div className="flex items-center gap-3 rounded-2xl border bg-muted/30 p-3 text-sm">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading your datasets…</span>
          </div>
        )}

        {loadError && (
          <div className="space-y-3">
            <Banner variant="error">
              <span>{loadError}</span>
            </Banner>
            <Button type="button" variant="outline" onClick={() => navigate('/onboarding/connect', { replace: true })}>
              Back to Connect
            </Button>
          </div>
        )}

        {datasets !== null && datasets.length === 0 && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-2xl border bg-muted/30 p-4 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="font-medium">No datasets found in your Meta business.</p>
                <p className="mt-1 text-muted-foreground">
                  Open Meta Events Manager, create a dataset (formerly a Pixel), then come back and click Reconnect.
                </p>
                <a
                  href="https://business.facebook.com/events_manager2/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-foreground underline underline-offset-2 hover:no-underline"
                >
                  Open Events Manager <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate('/onboarding/connect', { replace: true })}>
              Back to Connect
            </Button>
          </div>
        )}

        {datasets !== null && datasets.length > 0 && (
          <>
            <div className="space-y-3">
              {datasets.map((d) => {
                const isSelected = selected === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setSelected(d.id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all',
                      isSelected
                        ? 'border-dark/40 bg-card shadow-card'
                        : 'border-dark/10 bg-card hover:bg-bg-tint',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-dark">{d.name}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        ID {d.id}
                        {d.ad_account_count > 0 && ` · ${d.ad_account_count} ad account${d.ad_account_count === 1 ? '' : 's'}`}
                        {d.last_fired_time && ` · last event ${d.last_fired_time.split('T')[0]}`}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                        <Check className="h-3.5 w-3.5 text-green-700" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {submitError && (
              <Banner variant="error">
                <span>{submitError}</span>
              </Banner>
            )}

            <Button
              type="button"
              className="w-full rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold"
              disabled={!selected || submitting}
              onClick={handleContinue}
            >
              {submitting ? 'saving…' : 'Continue'}
            </Button>
          </>
        )}
      </div>
    </OnboardingShell>
  );
}
